import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { MarketCard } from '../components/cards/MarketCard';
import { useMarketSearch } from '../hooks/useMarketSearch';
import { usePageTitle } from '../hooks/usePageTitle';

export const Search: React.FC = () => {
  usePageTitle('Search Markets');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Fetch search results
  const { data: searchResults, isLoading } = useMarketSearch(debouncedQuery);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Immediately trigger search on Enter
      setDebouncedQuery(searchQuery);
    }
  }, [searchQuery]);

  const displayMarkets = searchResults?.markets || [];

  return (
    <Box pb={10}>
      <Box px={2} pt={2} mb={3}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Search Markets
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Find markets by name
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search for markets..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box px={2}>
          {!searchQuery ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="40vh"
            >
              <SearchIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom textAlign="center">
                Start Searching
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Use the search box above to find markets
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
                Or browse markets from the Browse tab
              </Typography>
            </Box>
          ) : searchQuery.length < 4 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="40vh"
            >
              <Typography variant="h6" gutterBottom>
                Keep Typing...
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Enter at least 4 characters to search
              </Typography>
            </Box>
          ) : displayMarkets.length > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {displayMarkets.length} result{displayMarkets.length !== 1 ? 's' : ''} found
              </Typography>
              <Grid container spacing={2}>
                {displayMarkets.map((market: any) => (
                  <Grid item xs={12} key={market.id}>
                    <MarketCard market={market} showCountry={true} />
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="40vh"
            >
              <Typography variant="h6" gutterBottom>
                No Results Found
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Try a different search term or browse markets from the Browse tab
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};